import type { Step } from 'react-joyride';

/**
 * Stage-specific walkthrough copy for the compact, three-stage Services
 * Post-Award Actions workflow. The workspace itself renders different forms
 * after routing, so its walkthrough deliberately explains the stable controls
 * (route, required fields, export, and human review) without assuming a
 * particular action has already been selected.
 */
export function getPostAwardActionTourSteps(stageIndex: number): Step[] {
  if (stageIndex === 0) {
    return [
      {
        target: '[data-tour-id="post-award-intake-title"]',
        placement: 'bottom',
        title: 'Contract & Package Intake',
        content: 'Start with the contract record or the source package that anchors this draft-only post-award action. Nothing is issued or sent from this workflow.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-intake-source"]',
        placement: 'bottom',
        title: 'Choose the source of record',
        content: 'Use an authorized contract record when it is available. Otherwise upload the governing award, PWS/SOW, prior modifications, and other relevant package files. Uploaded files are saved to this workflow for the later draft.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-intake-need"]',
        placement: 'top',
        title: 'Describe the need',
        content: 'Explain what changed or what support you need. This saved description gives the Guided Action Recommendation the facts it needs to propose a route.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-intake-continue"]',
        placement: 'top',
        title: 'Continue to route review',
        content: 'Continue once the required source and need are present. You can return here later; edits save on blur.',
        skipBeacon: true,
        locale: { last: 'Done' },
      },
    ];
  }

  if (stageIndex === 1) {
    return [
      {
        target: '[data-tour-id="post-award-recommendation-title"]',
        placement: 'bottom',
        title: 'Guided Action Recommendation',
        content: 'Review a draft-only recommendation based on the saved intake. The recommendation supports your decision; it does not approve, issue, or send an action.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-recommendation-actions"]',
        placement: 'bottom',
        title: 'Refresh the recommendation',
        content: 'Regenerate after updating the saved need or sources. Use Saved Work only when the information already stored in this workflow is the appropriate basis for the recommendation.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-intake-summary"]',
        placement: 'top',
        title: 'Check the saved intake',
        content: 'Verify the contract/package, sources, and need before choosing a route. Return to intake if a material fact is missing or incorrect.',
        skipBeacon: true,
      },
      {
        target: '[data-tour-id="post-award-route-choice"]',
        placement: 'top',
        title: 'Choose and confirm a route',
        content: 'Choose the route that fits the need, resolve any displayed blockers, then confirm it. Confirmation opens the matching draft-only workspace; human review remains required.',
        skipBeacon: true,
        locale: { last: 'Done' },
      },
    ];
  }

  return [
    {
      target: '[data-tour-id="post-award-workspace-title"]',
      placement: 'bottom',
      title: 'Draft-only Action Workspace',
      content: 'This workspace adapts to the route you confirmed. It collects facts and prepares working drafts for human agency review; it does not issue, approve, sign, or send an action.',
      skipBeacon: true,
    },
    {
      target: '[data-tour-id="post-award-workspace-route"]',
      placement: 'bottom',
      title: 'Follow the selected route',
      content: 'The workspace shows only the action-specific form or handoff relevant to the selected route. Use Back to recommendation if the route no longer fits the case.',
      skipBeacon: true,
    },
    {
      target: '[data-tour-id="post-award-workspace-form"]',
      placement: 'top',
      title: 'Complete required facts',
      content: 'Fields marked with an asterisk are required before a working draft can be exported. Values save on blur and remain editable.',
      skipBeacon: true,
    },
    {
      target: '[data-tour-id="post-award-workspace-review"]',
      placement: 'top',
      title: 'Export for human review',
      content: 'When the required facts are complete, export the working draft and complete signatures on the exported form where applicable. A human reviewer must still validate the package before any official action.',
      skipBeacon: true,
      locale: { last: 'Done' },
    },
  ];
}
